import { useState } from 'react';
import { useDraggable, useDroppable, useDndContext } from '@dnd-kit/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  Check,
  ClipboardList,
  GripVertical,
  Inbox,
  MapPin,
  X,
} from 'lucide-react';
import type {
  AssignmentPurpose,
  CalendarUnassignedJob,
} from '@gutterinv/shared';
import { postCalendarAssignment } from '../api/client.js';
import { PeopleMultiSelect } from './PeopleMultiSelect.js';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from './ui/card.js';
import { Badge } from './ui/badge.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { Skeleton } from './ui/skeleton.js';
import { cn } from '../lib/utils.js';
import { toISO } from '../lib/calendar-dates.js';
import type { DropTarget } from '../lib/calendar-drop.js';
import type { CalendarDragData } from '../hooks/useCalendarDnd.js';

/**
 * Two-tab sidebar for unassigned work:
 *   Installs   — jobs status='accepted' with NO install assignment (any date).
 *   Estimates  — jobs status='created' with NO estimate assignment (any date).
 *
 * Drag source: each card is a @dnd-kit useDraggable carrying an 'unassigned'
 * payload + the job's display fields (for the optimistic placeholder on drop).
 * Drop target: the sidebar itself is a droppable that, when an existing-chip
 * ('reassign') drag is released over it, unassigns the job (handled centrally
 * in useCalendarDnd). It ignores sidebar-originated drags.
 */
export function CalendarUnassignedSidebar({
  installs,
  estimates,
  isLoading,
}: {
  installs: CalendarUnassignedJob[];
  estimates: CalendarUnassignedJob[];
  isLoading: boolean;
}) {
  const [activeTab, setActiveTab] = useState<AssignmentPurpose>('install');
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);

  const { setNodeRef, isOver } = useDroppable({
    id: 'sidebar-unassign',
    data: { kind: 'sidebar' } satisfies DropTarget,
  });
  // Only show the unassign affordance when an EXISTING calendar chip is over us;
  // sidebar-originated ('unassigned'/'task') drags must not register.
  const { active } = useDndContext();
  const activeKind = (active?.data.current as CalendarDragData | undefined)?.payload.kind;
  const showUnassign = isOver && activeKind === 'reassign';

  const visible = activeTab === 'install' ? installs : estimates;
  const emptyLabel =
    activeTab === 'install'
      ? {
          title: 'All accepted jobs scheduled',
          hint: 'Every accepted job has a crew + date.',
        }
      : {
          title: 'All new jobs have estimates scheduled',
          hint: 'Office has dispatched every new job for a measure.',
        };

  return (
    <Card
      ref={setNodeRef}
      className={cn(
        'w-[22.5rem] shrink-0 flex flex-col overflow-hidden transition-colors',
        showUnassign && 'border-primary ring-2 ring-primary/30',
      )}
    >
      {showUnassign && (
        <div className="border-b border-primary/40 bg-primary/10 px-3 py-2 text-center text-xs font-medium text-primary">
          Drop here to unassign
        </div>
      )}
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          Unassigned
        </CardTitle>
        <div className="mt-2 flex gap-1 rounded-lg border border-border bg-secondary/30 p-0.5">
          <button
            type="button"
            onClick={() => {
              setActiveTab('install');
              setExpandedJobId(null);
            }}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors',
              activeTab === 'install'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <CalendarDays className="h-3 w-3" />
            Installs
            {installs.length > 0 && (
              <Badge variant="secondary" className="tnum ml-0.5 h-4 px-1 text-[0.625rem]">
                {installs.length}
              </Badge>
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              setActiveTab('estimate');
              setExpandedJobId(null);
            }}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors',
              activeTab === 'estimate'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <ClipboardList className="h-3 w-3" />
            Estimates
            {estimates.length > 0 && (
              <Badge variant="secondary" className="tnum ml-0.5 h-4 px-1 text-[0.625rem]">
                {estimates.length}
              </Badge>
            )}
          </button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto pt-0">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
            <Skeleton className="h-16" />
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Inbox className="mb-2 h-8 w-8 text-muted-foreground/40" />
            <div className="text-sm font-medium text-muted-foreground">
              {emptyLabel.title}
            </div>
            <div className="text-xs text-muted-foreground">{emptyLabel.hint}</div>
          </div>
        ) : (
          <div className="space-y-2">
            {visible.map((job) => (
              <UnassignedJobCard
                key={`${activeTab}-${job.id}`}
                job={job}
                purpose={activeTab}
                isExpanded={expandedJobId === job.id}
                onToggle={() =>
                  setExpandedJobId(expandedJobId === job.id ? null : job.id)
                }
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UnassignedJobCard({
  job,
  purpose,
  isExpanded,
  onToggle,
}: {
  job: CalendarUnassignedJob;
  purpose: AssignmentPurpose;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const queryClient = useQueryClient();

  // Phase 2.4: assignments are to PEOPLE. Uniqueness is now (job, purpose,
  // start_date), not per-crew — so there's no sibling-crew dedup here; a
  // duplicate start-date 409s server-side and surfaces in the error line.
  const [personIds, setPersonIds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState(() => toISO(new Date()));
  const [endDate, setEndDate] = useState(() => toISO(new Date()));

  // Drag source — carries the job's display fields so the drop's optimistic
  // placeholder shows real text immediately (no scavenger lookup).
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `unassigned-${purpose}-${job.id}`,
    data: {
      payload: { kind: 'unassigned', jobId: job.id, purpose },
      overlayLabel: job.displayName,
      job: {
        displayName: job.displayName,
        address: job.address,
        customerName: job.customerName,
        quoteAmountCents: job.quoteAmountCents,
      },
    } satisfies CalendarDragData,
  });

  const assignMutation = useMutation({
    mutationFn: postCalendarAssignment,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'assignments'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'unassigned'] });
      onToggle();
    },
  });

  const isEstimate = purpose === 'estimate';
  const todayISO = toISO(new Date());
  const startInvalid = !!startDate && startDate < todayISO;
  const endInvalid = !isEstimate && !!endDate && endDate < startDate;
  const saveDisabled =
    assignMutation.isPending ||
    personIds.length === 0 ||
    !startDate ||
    (!isEstimate && !endDate) ||
    startInvalid ||
    endInvalid;

  const handleSave = () => {
    if (personIds.length === 0 || !startDate) return;
    // Estimates collapse to single-day on submit.
    const effectiveEnd = isEstimate ? startDate : endDate;
    if (!effectiveEnd) return;
    assignMutation.mutate({
      jobId: job.id,
      personIds,
      startDate,
      endDate: effectiveEnd,
      purpose,
    });
  };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{ touchAction: 'none', opacity: isDragging ? 0.4 : 1 }}
      className={cn(
        'select-none rounded-lg border bg-card p-2.5 transition-colors cursor-grab active:cursor-grabbing',
        isEstimate
          ? 'border-dashed border-warning/50 hover:border-warning hover:bg-warning/[0.06]'
          : 'border-border hover:border-primary/30 hover:bg-primary/[0.04]',
      )}
    >
      <div className="flex items-center gap-1.5">
        <GripVertical className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />
        <div className="text-sm font-semibold leading-tight text-foreground truncate">
          {job.displayName}
        </div>
      </div>
      <div className="mt-0.5 truncate text-xs text-muted-foreground">
        {job.customerName}
      </div>
      <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground/80">
        <MapPin className="h-3 w-3 shrink-0" />
        <span className="truncate">{job.address}</span>
      </div>

      <Button
        variant="outline"
        size="sm"
        className={cn(
          'mt-2 h-7 w-full text-xs transition-all duration-200',
          isExpanded && 'hidden',
        )}
        onClick={onToggle}
      >
        {isEstimate ? 'Schedule estimate' : 'Schedule Job'}
      </Button>
      <div
        className={cn(
          'grid transition-all duration-200 ease-in-out',
          isExpanded
            ? 'grid-rows-[1fr] opacity-100 mt-2'
            : 'grid-rows-[0fr] opacity-0 mt-0',
        )}
      >
        <div className="overflow-hidden">
          <div className="space-y-2 rounded-md border border-border bg-secondary/30 p-2">
            <PeopleMultiSelect
              value={personIds}
              onChange={setPersonIds}
              disabled={assignMutation.isPending}
            />

            {isEstimate ? (
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                min={todayISO}
                aria-invalid={startInvalid}
                className={cn(
                  'h-8 text-xs',
                  startInvalid && 'border-destructive focus-visible:ring-destructive',
                )}
                disabled={assignMutation.isPending}
              />
            ) : (
              <div className="grid grid-cols-2 gap-1.5">
                <div>
                  <label className="mb-0.5 block text-[0.625rem] uppercase tracking-wider text-muted-foreground">
                    Start
                  </label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      const v = e.target.value;
                      setStartDate(v);
                      if (endDate && v > endDate) setEndDate(v);
                    }}
                    min={todayISO}
                    aria-invalid={startInvalid}
                    className={cn(
                      'h-8 text-xs',
                      startInvalid && 'border-destructive focus-visible:ring-destructive',
                    )}
                    disabled={assignMutation.isPending}
                  />
                </div>
                <div>
                  <label className="mb-0.5 block text-[0.625rem] uppercase tracking-wider text-muted-foreground">
                    End
                  </label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || todayISO}
                    aria-invalid={endInvalid}
                    className={cn(
                      'h-8 text-xs',
                      endInvalid && 'border-destructive focus-visible:ring-destructive',
                    )}
                    disabled={assignMutation.isPending || !startDate}
                  />
                </div>
              </div>
            )}
            {(startInvalid || endInvalid) && (
              <p className="text-[0.625rem] text-destructive">
                {startInvalid
                  ? 'Start date cannot be in the past.'
                  : 'End date must be on or after start date.'}
              </p>
            )}

            {assignMutation.error && (
              <div className="text-[0.625rem] text-destructive">
                {assignMutation.error instanceof Error
                  ? assignMutation.error.message
                  : 'Failed'}
              </div>
            )}

            <div className="flex gap-1.5">
              <Button
                size="sm"
                className="h-7 flex-1 text-xs"
                disabled={saveDisabled}
                onClick={handleSave}
              >
                <Check className="h-3 w-3" />
                {assignMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                disabled={assignMutation.isPending}
                onClick={onToggle}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
